/**
 * 主应用组件
 */
import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import ProtectedRoute, { PaidRoute } from './components/ProtectedRoute';
import TopNav from './components/layout/TopNav'; // 保留兼容
import Sidebar from './components/layout/Sidebar';
import TopBar from './components/layout/TopBar';
import Login from './pages/Login';
import Register from './pages/Register';
import ProjectList from './pages/ProjectList';
import ProjectDetail from './pages/ProjectDetail';
import Profile from './pages/Profile';
import ModelConfigPage from './pages/ModelConfig';
import ProjectWizard from './pages/ProjectWizard';
import BidWritingWizard from './pages/BidWritingWizard';
import HomePage from './pages/HomePage';
import AdminRoute from './components/AdminRoute';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminUserDetail from './pages/admin/AdminUserDetail';
import AdminOrders from './pages/admin/AdminOrders';
import AdminOrderDetail from './pages/admin/AdminOrderDetail';
import AdminPlans from './pages/admin/AdminPlans';
import AdminModels from './pages/admin/AdminModels';
import AdminPrompts from './pages/admin/AdminPrompts';
import AdminSettings from './pages/admin/AdminSettings';
import AdminSkills from './pages/admin/AdminSkills';
import AdminDiagramStyles from './pages/admin/AdminDiagramStyles';
import UserPrompts from './pages/UserPrompts';
import SearchConfigPage from './pages/SearchConfigPage';
import MediaLibrary from './pages/MediaLibrary';
import UserSkills from './pages/UserSkills';
import WordPreviewPage from './pages/WordPreview';
import BidWritingWord from './pages/BidWritingWord';
import ToastContainer from './components/ToastContainer';
import ConfirmDialog from './components/ConfirmDialog';
import PricingModal from './components/PricingModal';
import { authApi } from './services/authApi';

import ProjectEdit from './pages/ProjectEdit';
import TemplateList from './pages/TemplateList';
import TemplateCreate from './pages/TemplateCreate';
import TemplateDetail from './pages/TemplateDetail';

/** 受保护页面布局：侧栏 + 顶栏 + 内容区 */
const AppLayout: React.FC = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  return (
    <div className="app-layout">
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <div className="app-main">
        <TopBar onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)} />
        <div className="app-content">
          <Outlet />
        </div>
      </div>
      <PricingModal />
    </div>
  );
};

function App() {
  const token = useAuthStore((state) => state.token);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const setAuth = useAuthStore((state) => state.setAuth);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    console.log('App 组件状态变化:', { token: !!token, isAuthenticated });
  }, [token, isAuthenticated]);

  useEffect(() => {
    if (!isInitialized && token) {
      authApi
        .getMe()
        .then((response) => {
          setAuth(token, response.data);
          setIsInitialized(true);
        })
        .catch((error) => {
          console.error('Token 验证失败:', error);
          useAuthStore.getState().clearAuth();
          setIsInitialized(true);
        });
    } else if (!isInitialized) {
      setIsInitialized(true);
    }
  }, [token, isAuthenticated, setAuth, isInitialized]);

  return (
    <>
      <Routes>
        {/* 公开路由 */}
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to="/home" replace /> : <Login />}
        />
        <Route
          path="/register"
          element={isAuthenticated ? <Navigate to="/home" replace /> : <Register />}
        />
        <Route path="/pricing" element={<Navigate to="/projects" replace />} />

        {/* 受保护的工作台路由 */}
        <Route element={<AppLayout />}>
          <Route path="/home" element={<HomePage />} />
          <Route path="/projects" element={<PaidRoute><ProjectList /></PaidRoute>} />
          <Route path="/projects/new" element={<PaidRoute><ProjectWizard /></PaidRoute>} />
          <Route path="/projects/:id" element={<PaidRoute><ProjectDetail /></PaidRoute>} />
          <Route path="/projects/:id/edit" element={<PaidRoute><ProjectEdit /></PaidRoute>} />
          <Route path="/projects/:id/wizard" element={<PaidRoute><BidWritingWizard /></PaidRoute>} />
          <Route path="/projects/:id/word" element={<PaidRoute><BidWritingWord /></PaidRoute>} />
          <Route path="/projects/:id/preview" element={<PaidRoute><WordPreviewPage /></PaidRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/models" element={<PaidRoute><ModelConfigPage /></PaidRoute>} />
          <Route path="/templates" element={<PaidRoute><TemplateList /></PaidRoute>} />
          <Route path="/templates/new" element={<PaidRoute><TemplateCreate /></PaidRoute>} />
          <Route path="/templates/:id" element={<PaidRoute><TemplateDetail /></PaidRoute>} />
          <Route path="/user/prompts" element={<PaidRoute><UserPrompts /></PaidRoute>} />
          <Route path="/search-config" element={<ProtectedRoute><SearchConfigPage /></ProtectedRoute>} />
          <Route path="/media" element={<PaidRoute><MediaLibrary /></PaidRoute>} />
          <Route path="/skills" element={<ProtectedRoute><UserSkills /></ProtectedRoute>} />
        </Route>

        {/* 管理员后台 */}
        <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
          <Route index element={<AdminDashboard />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="users/:id" element={<AdminUserDetail />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="orders/:id" element={<AdminOrderDetail />} />
          <Route path="plans" element={<AdminPlans />} />
          <Route path="models" element={<AdminModels />} />
          <Route path="prompts" element={<AdminPrompts />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="skills" element={<AdminSkills />} />
          <Route path="diagram-styles" element={<AdminDiagramStyles />} />
        </Route>

        {/* 默认重定向 */}
        <Route
          path="/"
          element={isAuthenticated ? <Navigate to="/projects" replace /> : <Navigate to="/login" replace />}
        />
        <Route path="*" element={<Navigate to="/projects" replace />} />
      </Routes>

      <ToastContainer />
      <ConfirmDialog />
    </>
  );
}

export default App;
